import React, { FC, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import get from 'get-value'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState, UserInfoContext } from '../../Common/Contexts'
import { Presence as PresenceLanguage } from '../../../../i18n'
import { PresenceHeader } from '../Components/PresenceHeader'
import {
    BackendPresenceFilter,
    Presence,
    PresenceFilters,
    PresencesGrouped,
    PresenceWebSocketRequests,
    PresenceWebSocketResponses,
    UserPresence,
    UserPresences
} from '../Types/Types'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import {
    Availability,
    DEFAULT_PRESENCE_LIMIT,
    MAX_SEARCH_QUERY_LENGTH,
    PRESENCE_FILTERS_LOCAL_STORAGE_KEY,
    PRESENCE_FILTERS_SORT_BY_LOCAL_STORAGE_KEY,
    PresenceFilter,
    SortingOptions
} from '../Constant/Constants'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { PresenceUsers } from '../Components/PresenceUsers'
import { AkixiWebSocket } from '../../Common/API/Websockets/AkixiWebSocket'
import { usePersistentState } from '../../Common/Hooks/usePersistState'
import { WebsocketMessageType } from '../../Common/API/Websockets/Types/Types'
import { $debug, $error } from '../../Common/Utils/ConsoleUtils'
import { DevicesResponse, SessionAPIResponse } from '../../../NonReact/Types/Types'
import { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { DeviceFields } from '../../../NonReact/constants/DeviceConstants'
import { DeviceTypeIdString } from '../../../NonReact/constants/DeviceTypes'
import { ReactStateRef, useStateAsRef } from '../../Common/Hooks/useStateAsRef'
import { getSession } from '../../Common/API/CommonAPI'
import { presenceWebsocketHandler } from '../PresenceWebsocketHandlers/PresenceWebsocketHandler'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import Loader from '../../Common/Components/Loader'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { PRESENCE_WEBSOCKET_URL } from '../../../NonReact/constants/ApiConstants'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { getDevicesFromTelSys } from '../../Common/API/EntityApiClient'

type Props = {
    isDarkModeSwitch?: boolean
}

/**
 * UserPresencePage component
 */
export const UserPresencePage: FC<Props> = ({ isDarkModeSwitch }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(PresenceLanguage.NAME)

    const { displayWarningMessage, displayError }: MessageContextType = useContext(MessageContext)

    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    const [openWebsocket, setOpenWebsocket]: ReactState<
        Nullable<AkixiWebSocket<PresenceWebSocketResponses, PresenceWebSocketRequests>>
    > = useState<Nullable<AkixiWebSocket<PresenceWebSocketResponses, PresenceWebSocketRequests>>>()

    const sortModeState: ReactState<SortingOptions> = usePersistentState<SortingOptions>(
        PRESENCE_FILTERS_SORT_BY_LOCAL_STORAGE_KEY,
        SortingOptions.ASC
    )
    const filterState: ReactState<PresenceFilters> = usePersistentState<PresenceFilters>(
        PRESENCE_FILTERS_LOCAL_STORAGE_KEY,
        {
            availability: [],
            departments: [],
            queues: []
        }
    )
    const searchState: ReactState<string> = useState<string>(EMPTY_STRING)
    const [currentPageRef, setCurrentPage, currentPage]: ReactStateRef<number> = useStateAsRef<number>(0)
    const [maxPageRef, setMaxPage, maxPage]: ReactStateRef<number> = useStateAsRef<number>(0)

    const [departments, setDepartments]: ReactState<DevicesResponse[]> = useState<DevicesResponse[]>([])
    const [queues, setQueues]: ReactState<DevicesResponse[]> = useState<DevicesResponse[]>([])

    const [isWebsocketOpen, setIsWebsocketOpen]: ReactState<boolean> = useState<boolean>(false)

    const [countsByAvailability, setCountsByAvailability]: ReactState<{ [key in Availability]: number }> = useState<{
        [key in Availability]: number
    }>({
        Available: 0,
        Busy: 0,
        Away: 0,
        DoNotDisturb: 0,
        BeRightBack: 0,
        OutOfOffice: 0,
        Offline: 0
    })

    const [userPresences, setUserPresences]: ReactState<Nullable<PresencesGrouped>> =
        useState<Nullable<PresencesGrouped>>()

    const [filters]: ReactState<PresenceFilters> = filterState
    const [sortMode]: ReactState<SortingOptions> = sortModeState
    const [searchQuery]: ReactState<string> = searchState

    const [sessionID, setSessionID]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(true)
    const [isContentUpdating, setIsContentUpdating]: ReactState<boolean> = useState<boolean>(true)

    const sortPresencesBy = useCallback(
        (presences: UserPresences, key: string): UserPresences =>
            // Sort the presences based on the sort mode
            presences.sort((a: UserPresence, b: UserPresence): number => {
                const viewModeA: string | Presence = (get(a, key) as string | Presence) ?? EMPTY_STRING
                const viewModeB: string | Presence = (get(b, key) as string | Presence) ?? EMPTY_STRING

                if (typeof viewModeA === 'string' && typeof viewModeB === 'string') {
                    return sortMode === SortingOptions.ASC
                        ? viewModeA.localeCompare(viewModeB)
                        : viewModeB.localeCompare(viewModeA)
                }

                return 0
            }),
        [sortMode]
    )

    const filteredGroupedPresencesSortedByAvailability: [Availability, UserPresences][] = useMemo(
        (): [Availability, UserPresences][] =>
            (Object.entries(userPresences ?? {}) as [Availability, UserPresences][]).sort(
                (
                    [availabilityA]: [Availability, UserPresences],
                    [availabilityB]: [Availability, UserPresences]
                ): number => {
                    const orderA: number = Object.values(Availability).indexOf(availabilityA ?? EMPTY_STRING)
                    const orderB: number = Object.values(Availability).indexOf(availabilityB ?? EMPTY_STRING)

                    return orderA - orderB
                }
            ),
        [JSON.stringify(userPresences)]
    )

    // Sorted by Name
    const filteredGroupedPresencesSortedByName: [Availability, UserPresences][] = useMemo(
        (): [Availability, UserPresences][] =>
            filteredGroupedPresencesSortedByAvailability.map(
                ([availability, userPresencesLocal]: [Availability, UserPresences]): [Availability, UserPresences] => [
                    availability,
                    sortPresencesBy(userPresencesLocal, 'name')
                ]
            ),
        [JSON.stringify(filteredGroupedPresencesSortedByAvailability), sortMode]
    )

    const isMorePagination: boolean = useMemo((): boolean => currentPage < maxPage, [currentPage, maxPage])

    const handleFilterUpdate = useCallback(
        (newPage: number = 0, shouldLoad: boolean = true): void => {
            setIsLoading(shouldLoad)
            setIsContentUpdating(true)

            if (searchQuery.length > MAX_SEARCH_QUERY_LENGTH) {
                displayWarningMessage(language.get(PresenceLanguage.Presence_SearchQuery_TooLong))
                setIsContentUpdating(false)
                setIsLoading(false)
                return
            }

            openWebsocket?.sendMessage({
                type: IsVarEmpty(userPresences) ? WebsocketMessageType.CREATE : WebsocketMessageType.CHANGE,
                sessionID: sessionID ?? EMPTY_STRING,
                data: {
                    startIndex: newPage * DEFAULT_PRESENCE_LIMIT,
                    recsPerPage: DEFAULT_PRESENCE_LIMIT,
                    filter: [
                        ...filters.availability.map(
                            (availability: string): BackendPresenceFilter => ({
                                name: PresenceFilter.AVAILABILITY,
                                value: availability
                            })
                        ),
                        ...filters.departments.map(
                            (department: string): BackendPresenceFilter => ({
                                name: PresenceFilter.DEPARTMENT,
                                value: department
                            })
                        ),
                        ...filters.queues.map(
                            (queue: string): BackendPresenceFilter => ({
                                name: PresenceFilter.QUEUE,
                                value: queue
                            })
                        )
                    ],
                    searchQuery
                }
            })
        },
        [filters, openWebsocket, userPresences, sessionID, searchQuery]
    )

    const handlePageChange = (newPage: number): void => {
        if (newPage <= maxPageRef.current && newPage >= 0) {
            setCurrentPage(newPage)
            handleFilterUpdate(newPage, false)
        }
    }

    const incrementPage = (): void => handlePageChange(currentPageRef.current + 1)

    const connectToWebsocket = useCallback((): void => {
        openWebsocket?.unsubscribe()

        $debug(`Connecting to websocket`)

        const akixiWebSocket: AkixiWebSocket<PresenceWebSocketResponses, PresenceWebSocketRequests> =
            AkixiWebSocket.newInstance<PresenceWebSocketResponses, PresenceWebSocketRequests>(PRESENCE_WEBSOCKET_URL)

        akixiWebSocket.webSocket.addEventListener('open', (): void => {
            $debug(`Websocket connected`)
            setIsWebsocketOpen(true)
        })

        setOpenWebsocket(akixiWebSocket)

        akixiWebSocket.subscribe(
            (response: PresenceWebSocketResponses): void => {
                if (isVarNotEmpty(response)) {
                    setCountsByAvailability(response.data.countByGrouping)

                    if (isVarNotEmpty(response.data.count)) {
                        setMaxPage(Math.ceil(response.data.count / DEFAULT_PRESENCE_LIMIT) - 1)
                    }

                    setUserPresences(
                        (oldPresences: Nullable<PresencesGrouped>): Nullable<PresencesGrouped> =>
                            presenceWebsocketHandler(oldPresences, response)
                    )
                }

                setIsContentUpdating(false)
                setIsLoading(false)
            },
            (error: Error): void => {
                displayError(error)

                akixiWebSocket.webSocket.close()

                setIsContentUpdating(false)
                setIsLoading(false)
            }
        )

        /* This is for local dev. You also need to remove the URL from the websocket call to enable this. */
        // akixiWebSocket.addMockMessages(
        //     {
        //         messageToServer: {
        //             type: WebsocketMessageType.CREATE,
        //             sessionID: sessionID ?? EMPTY_STRING,
        //             data: {
        //                 startIndex: 0,
        //                 recsPerPage: 100,
        //                 filter: [],
        //                 searchQuery: ''
        //             }
        //         },
        //         messageFromServer: {
        //             type: WebsocketMessageType.CREATE,
        //             data: mockUsers as PresenceResponse
        //         }
        //     },
        //     {
        //         messageToServer: {
        //             type: WebsocketMessageType.CREATE,
        //             sessionID: sessionID ?? EMPTY_STRING,
        //             data: {
        //                 startIndex: 0,
        //                 recsPerPage: 100,
        //                 filter: [{ name: PresenceFilter.AVAILABILITY, value: 'Away' }],
        //                 searchQuery: ''
        //             }
        //         },
        //         messageFromServer: {
        //             type: WebsocketMessageType.CREATE,
        //             data: {} as PresenceResponse
        //         }
        //     },
        //     {
        //         delay: 3000,
        //         messageFromServer: {
        //             type: WebsocketMessageType.UPDATE,
        //             data: updateUsers as PresenceUpdateResponse
        //         }
        //     }
        // )
        //     {
        //         delay: 6000,
        //         messageFromServer: {
        //             type: WebsocketMessageType.ADD,
        //             data: addUsers as PresenceResponse
        //         }
        //     },
        //     {
        //         delay: 9000,
        //         messageFromServer: {
        //             type: WebsocketMessageType.REMOVE,
        //             data: removeUsers as PresenceDeleteResponse
        //         }
        //     },
        //     {
        //         delay: 12000,
        //         messageFromServer: {
        //             type: WebsocketMessageType.CHANGE,
        //             data: paginateUsers as PresenceResponse
        //         }
        //     },
        //     {
        //         delay: 15000,
        //         messageFromServer: {
        //             type: WebsocketMessageType.CHANGE,
        //             data: mockUsers as PresenceResponse
        //         }
        //     }
        // )

        setIsLoading(true)
    }, [filters, sessionID, userPresences])

    useEffect((): void => {
        if (openWebsocket?.isWebsocketOpen() && isStringNotEmpty(sessionID) && isWebsocketOpen) {
            handleFilterUpdate()
        }
    }, [filters, openWebsocket, sessionID, isWebsocketOpen, searchQuery])

    useEffect((): void => {
        if (
            userInfo &&
            userInfo.Scope.TelSysID?.length > 0 &&
            userInfo.Scope.PartIDInTelSys?.length > 0 &&
            userInfo.Scope.TelSysID?.[0] !== ALL_VALUE
        ) {
            getDevicesFromTelSys(
                userInfo.Scope.TelSysID?.[0],
                userInfo.Scope.PartIDInTelSys,
                true,
                [`${DeviceTypeIdString.GroupDepartment},${DeviceTypeIdString.GroupAcd}`],
                undefined,
                undefined,
                [DeviceFields.DEVICE_TYPE_ID]
            )
                .then((devices: Nullable<DevicesResponse[]>): void => {
                    setQueues(
                        devices?.filter(
                            (device: DevicesResponse): boolean =>
                                `${device.DeviceTypeID}` === DeviceTypeIdString.GroupAcd
                        ) ?? []
                    )
                    setDepartments(
                        devices?.filter(
                            (device: DevicesResponse): boolean =>
                                `${device.DeviceTypeID}` === DeviceTypeIdString.GroupDepartment
                        ) ?? []
                    )
                })
                .catch($error)
        }
    }, [userInfo])

    useEffect((): void => {
        if (isStringNotEmpty(sessionID)) {
            connectToWebsocket()
        }
    }, [sessionID])

    useEffect((): void => {
        getSession().then((session: JSONResponse<SessionAPIResponse>): void => {
            if (isStringNotEmpty(session?.SessionID)) {
                setSessionID(session.SessionID)
            }
        })
    }, [])

    return (
        <div className='user-presence'>
            <PresenceHeader
                isDarkModeSwitch={isDarkModeSwitch}
                sortModeState={sortModeState}
                filterState={filterState}
                searchState={searchState}
                countsByAvailability={countsByAvailability}
                departments={departments}
                queues={queues}
            />
            {isVarNotEmpty(filteredGroupedPresencesSortedByName) ? (
                <div className='loader-container'>
                    {isLoading ? <Loader className='pu-loader' /> : <></>}
                    <PresenceUsers
                        isMorePagination={isMorePagination}
                        incrementPage={incrementPage}
                        presenceGroups={filteredGroupedPresencesSortedByName}
                        isContentUpdating={isContentUpdating}
                    />
                </div>
            ) : (
                <></>
            )}
        </div>
    )
}
